import {
  READER_MAIN_LINK,
  STORIES_API_TYPE,
  TAGS_TYPE,
  CATEGORIES_TYPE,
  CREATORS_API_TYPE,
  CATALOG_PAGE_TAGS_LINK,
  CATALOG_PAGE_CATEGORIES_LINK,
  CREATOR_LINK,
  TAG_TYPE,
  CATEGORY_TYPE,
  CHILD_TYPE_BOLD,
  CHILD_TYPE_ITALIC,
  CHILD_TYPE_UNDERLINE,
  CHILD_TYPE_STRIKETHROUGH,
  CATALOG_PAGE_STORIES_LINK,
  WORDS_PER_MINUTE,
} from './constants';

export const formatNumber = (number) => number.toLocaleString('en-US');

export function getRandomId() {
  const ObjectId = (
    m = Math,
    d = Date,
    h = 16,
    s = (s) => m.floor(s).toString(h)
  ) => s(d.now() / 1000) + ' '.repeat(h).replace(/./g, () => s(m.random() * h));
  return ObjectId();
}

export function parseNode(node) {
  if (node.nodeType === 3) {
    // Text node
    return { type: 'text', content: node.textContent };
  } else if (node.nodeType === 1) {
    // Element node
    const children = Array.from(node.childNodes).map(parseNode);
    return {
      type: node.tagName.toLowerCase(),
      children,
    };
  }
  return null;
}

export function parseStoryHTML(htmlString, maxChars = 700) {
  // Handle plain text input
  if (!htmlString.includes('<')) {
    const truncated = htmlString.length > maxChars;
    let slicedText = htmlString;

    if (truncated) {
      let cutoffIndex = Math.min(maxChars, htmlString.length);
      const nextPeriod = htmlString.indexOf('.', cutoffIndex);
      if (nextPeriod !== -1 && nextPeriod - cutoffIndex < 50) {
        cutoffIndex = nextPeriod + 1;
      }
      slicedText = htmlString.slice(0, cutoffIndex);
    }

    return {
      elements: [
        {
          type: 'p',
          children: [{ type: 'text', content: slicedText }],
          key: 'element-0',
        },
      ],
      isTextTruncated: truncated,
    };
  }

  // Create a temporary div to parse HTML
  const div = document.createElement('div');

  // Get plain text to count characters
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = htmlString;
  const plainText = tempDiv.textContent;

  // Find a good breaking point (after a period) near our target length
  let cutoffIndex = Math.min(maxChars, plainText.length);
  const nextPeriod = plainText.indexOf('.', cutoffIndex);
  if (nextPeriod !== -1 && nextPeriod - cutoffIndex < 50) {
    // Look ahead up to 50 chars for a period
    cutoffIndex = nextPeriod + 1;
  }

  // Slice the HTML content
  let slicedHTML = htmlString;
  if (cutoffIndex < plainText.length) {
    // Find the corresponding position in the HTML
    let charCount = 0;
    let htmlIndex = 0;

    while (charCount < cutoffIndex && htmlIndex < htmlString.length) {
      if (htmlString[htmlIndex] === '<') {
        // Skip tags
        while (htmlIndex < htmlString.length && htmlString[htmlIndex] !== '>') {
          htmlIndex++;
        }
      } else {
        charCount++;
      }
      htmlIndex++;
    }

    // Find the end of the current tag if we're in the middle of one
    while (htmlIndex < htmlString.length && htmlString[htmlIndex - 1] !== '>') {
      htmlIndex++;
    }

    slicedHTML = htmlString.slice(0, htmlIndex);

    // Close any unclosed tags
    const matches = slicedHTML.match(/<(\w+)[^>]*>/g) || [];
    const closingTags = matches
      .reverse()
      .map((tag) => tag.match(/<(\w+)[^>]*>/)[1])
      .filter((tag) => !slicedHTML.includes(`</${tag}>`))
      .map((tag) => `</${tag}>`)
      .join('');

    slicedHTML += closingTags;
  }

  div.innerHTML = slicedHTML;

  const isTextTruncated = cutoffIndex < plainText.length;

  return {
    elements: Array.from(div.childNodes)
      .map((node, index) => ({ ...parseNode(node), key: `element-${index}` }))
      .filter(Boolean),
    isTextTruncated,
  };
}

export function renderStoryChildren(children) {
  return children.map((child, idx) => {
    let childElement = null;

    // Обработка обычного текста
    childElement = (
      <span
        className={`${child.text_type === CHILD_TYPE_BOLD ? 'bold' : ''} ${
          child.text_type === CHILD_TYPE_ITALIC ? 'italic' : ''
        } ${child.text_type === CHILD_TYPE_UNDERLINE ? 'underline' : ''} ${
          child.text_type === CHILD_TYPE_STRIKETHROUGH ? 'strikethrough' : ''
        }`}
        key={idx}
        dangerouslySetInnerHTML={{ __html: child?.text ?? '' }}
      />
    );

    return childElement;
  });
}

export function parseLink(type, _id) {
  return type === STORIES_API_TYPE
    ? `/${CATALOG_PAGE_STORIES_LINK}/${READER_MAIN_LINK}/${_id}`
    : type === TAGS_TYPE
    ? `/${CATALOG_PAGE_TAGS_LINK}/${TAG_TYPE}/${_id}`
    : type === CATEGORIES_TYPE
    ? `/${CATALOG_PAGE_CATEGORIES_LINK}/${CATEGORY_TYPE}/${_id}`
    : type === CREATORS_API_TYPE
    ? `/${CREATOR_LINK}/${_id}`
    : null;
}

export function calculateReadingTime(totalWords, minLabel, hoursLabel) {
  const totalMinutes = Math.ceil(totalWords / WORDS_PER_MINUTE);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours === 0) {
    return `${minutes} ${minLabel}`;
  }

  if (minutes === 0) {
    return `${hours} ${hoursLabel}`;
  }

  return `${hours} ${hoursLabel} ${minutes} ${minLabel}`;
}
